<template>
    <div class="content">
        <div class="container-fluid">
            <!-- Titre & breadcrumb -->
            <div class="page-heading row justify-content-between align-items-center py-3">
                <div class="col-lg-4">
                    <h4 class="page-title">{{ $t('componentEditHub.title') }}</h4>
                </div>
                <div class="col-auto order-2 d-none d-sm-block">
                    <ol class="breadcrumb m-0 py-2">
                        <li class="breadcrumb-item">
                            <router-link to="/installation-list">{{ $t('common.installerSpace') }}</router-link>
                        </li>
                        <li class="breadcrumb-item">
                            <router-link :to="pathToInstallationEditComponents">{{ $t('componentEditHub.breadcrumb') }}</router-link>
                        </li>
                        <li class="breadcrumb-item active">{{ $t('componentEditHub.title') }}</li>
                    </ol>
                </div>
            </div>

            <!-- Formulaire -->
            <form id="componentForm" name="componentForm" @submit.prevent="handleSubmit" class="pb-4">
                <div class="row">
                    <div class="col-xxl-8 col-xl-7 d-flex flex-column">
                        <div class="card flex-grow-1 mb-3">
                            <div class="card-header pb-0">
                                <h4 class="header-title">{{ $t('common.identification') }}</h4>
                            </div>
                            <div class="card-body">
                                <div class="row">
                                    <!-- Colonne gauche : Image -->
                                    <div class="col-md-6 d-flex flex-column justify-content-center align-items-center text-center mb-2 mb-md-0">
                                        <img :src="hub.image || '/default-hub.png'" alt="Image du hub"
                                            class="img-fluid rounded shadow-sm mb-2"
                                            style="max-height: 200px; object-fit: contain;" />
                                        <a :href="hub.documentationUrl" class="btn btn-outline-primary"
                                            target="_blank" rel="noopener noreferrer">
                                            {{ $t('common.downloadDoc') }}
                                        </a>
                                    </div>

                                    <!-- Colonne droite : Infos -->
                                    <div class="col-md-6">
                                        <div class="row">
                                            <div class="mb-2 col-12">
                                                <label for="nom" class="form-label">{{ $t('componentEditHub.hubName') }}</label>
                                                <input type="text" class="form-control" id="nom" required maxlength="32" v-model="hub.label" />
                                            </div>

                                            <div class="mb-2 col-12">
                                                <label for="numero-serie" class="form-label">{{ $t('common.serialNumber') }}</label>
                                                <input type="text" class="form-control" id="numero-serie" v-model="hub.serial_number" readonly />
                                            </div>

                                            <div class="mb-2 col-12">
                                                <label for="versions" class="form-label">{{ $t('common.versions') }}</label>
                                                <input type="text" class="form-control" id="versions" v-model="hub.getVersions" readonly />
                                            </div>

                                            <div class="mb-2 col-12">
                                                <label for="warrantyStartDate" class="form-label">{{ $t('common.warrantyStart') }}</label>
                                                <input type="text" class="form-control" id="warranty-start-date" v-model="hub.getWarrantyStartDate" readonly />
                                            </div>

                                            <div v-if="hub.mac_address" class="mb-2 col-12">
                                                <label for="mac-address" class="form-label">{{ $t('common.macAddress') }}</label>
                                                <input type="text" class="form-control" id="mac-address" v-model="hub.mac_address" readonly />
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </div>

                        <div class="card flex-grow-1 mb-3">
                            <div class="card-header pb-0">
                                <h4 class="header-title">{{ $t('common.generalConfig') }}</h4>
                            </div>
                            <div class="card-body">
                                <div class="row align-items-end">
                                    <div class="mb-2 col-md-6">
                                        <ExternalInput :label="$t('componentEditHub.tamperInput')" icon="" :inputs="inputs" v-model:inputValue="hub.auto_protection_input" />
                                    </div>
                                    <div class="mb-2 col-md-6">
                                        <ExternalInput :label="$t('componentEditHub.powerInput')" icon="" :inputs="inputs" v-model:inputValue="hub.power_test_input" />
                                    </div>
                                    <div class="mb-2 col-md-6">
                                        <label for="timezone" class="form-label">{{ $t('componentEditHub.timezone') }}</label>
                                        <input type="text" class="form-control" id="timezone" v-model="hub.timezone" readonly />
                                    </div>
                                </div>
                            </div>
                        </div>

                        <div class="card flex-shrink-0">
                            <div class="card-header pb-0">
                                <h4 class="header-title">{{ $t('componentEditHub.antiPassBackParams') }}</h4>
                            </div>
                            <div class="card-body">
                                <div class="row">
                                    <div class="mb-3 col-mt-2">
                                        <div class="d-flex justify-content-between align-items-center bg-primary-lighten rounded py-1 px-2">
                                            <label class="form-check-label flex-grow-1 me-2" for="anti-pass-back">{{ $t('common.antiPassBack') }}</label>
                                            <input type="checkbox" id="anti-pass-back" v-model="hub.anti_pass_back" data-switch="bool" />
                                            <label for="anti-pass-back" :data-on-label="$t('common.yes')" :data-off-label="$t('common.no')"></label>
                                        </div>
                                    </div>
                                    <div v-if="hub.anti_pass_back" class="mb-3 col-mt-2">
                                        <div class="d-flex justify-content-between align-items-center bg-primary-lighten rounded py-1 px-2">
                                            <label class="form-check-label flex-grow-1 me-2" for="anti-time-back">{{ $t('common.antiTimeBack') }}</label>
                                            <input type="checkbox" id="anti-time-back" v-model="antiTimeBackToggle" data-switch="bool" />
                                            <label for="anti-time-back" :data-on-label="$t('common.yes')" :data-off-label="$t('common.no')"></label>
                                        </div>
                                    </div>
                                    <div v-if="antiTimeBackToggle" class="mb-3 col-mt-2">
                                        <TempoWithUnit :label="$t('componentEditHub.antiTimeBackDelay')"
                                            v-model:value="hub.anti_time_back" unit="minute(s)" :min="1" :max="120" :show-unit="false">
                                        </TempoWithUnit>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>

                    <div class="col-xxl-4 col-xl-5">
                        <div class="card card-h-100">
                            <div class="card-header pb-0 d-flex align-items-center justify-content-between">
                                <h4 class="header-title">{{ $t('componentEditHub.composition') }}</h4>
                                <span v-if="hub.hub_state === 1" class="badge bg-success font-14 px-2">{{ $t('common.connected') }}</span>
                                <span v-else class="badge bg-danger font-14 px-2">{{ $t('common.disconnected') }}</span>
                            </div>
                            <div class="card-body ps-1">
                                <div ref="tree" class="component-treeview tree-components-lite">
                                    <ul>
                                        <li data-jstree='{ "icon" : "mdi mdi-server-network text-primary", "opened" : true }'>
                                            <div class="tree-row">
                                                <div id="hub_node" class="tree-cell component-name fw-bold text-primary">{{ hub.label }}</div>
                                                <div class="tree-cell component-action"></div>
                                            </div>
                                            <ul>
                                                <li v-for="reader in hub.readers" :key="reader.key(hub.id)" data-jstree='{ "icon" : "mdi mdi-key-wireless text-success" }'>
                                                    <div class="tree-row">
                                                        <div class="tree-cell component-name fw-bold text-dark">{{ reader.label }}</div>
                                                        <div class="tree-cell component-type">{{ reader.typeLabel }}</div>
                                                        <div class="tree-cell component-action">
                                                            <TreeButton :path="reader.path(facilityId, hub.id)" />
                                                        </div>
                                                    </div>
                                                </li>
                                                <li v-for="module in hub.extensions" :key="module.key(hub.id)" data-jstree='{ "icon" : "mdi mdi-key-wireless text-success" }'>
                                                    <div class="tree-row">
                                                        <div class="tree-cell component-name fw-bold text-dark">{{ module.label }}</div>
                                                        <div class="tree-cell component-type">{{ $t('common.relayModule') }}</div>
                                                        <div class="tree-cell component-action">
                                                            <TreeButton :path="module.path(facilityId, hub.id)" />
                                                        </div>
                                                    </div>
                                                </li>
                                            </ul>
                                        </li>
                                    </ul>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
                <!-- Form actions -->
                <div class="form-actions-sticky d-flex justify-content-center align-items-center">
                    <router-link :to="pathToInstallationEditComponents" class="btn btn-outline-secondary me-2">{{ $t('common.back') }}</router-link>
                    <button type="button" class="btn btn-outline-primary me-2" @click="updateFirmware">{{ $t('componentEditHub.firmwareUpdate') }}</button>
                    <button type="button" class="btn btn-danger me-2" @click="replaceOrDeleteModal.show()">{{ $t('componentEditHub.replaceOrDelete') }}</button>
                    <button type="submit" class="btn btn-primary">{{ $t('common.save') }}</button>
                </div>
            </form>
        </div>
        <ReplaceOrDeleteHubModal ref="replaceOrDeleteModal" :action="handleReplaceOrDelete" />
        <FirmwareUpdateModal ref="firmwareUpdateModal" />
    </div>
</template>

<script>
import { computed, reactive, ref, onMounted } from 'vue';
import { useRouter, useRoute } from 'vue-router';
import { useClientStore } from '@/stores/clientStore';
import { useUserStore } from '@/stores/userStore';
import { useI18n } from 'vue-i18n';
import { showToast } from '@/services/toast'
import ExternalInput from '@/components/externalInput.vue';
import TempoWithUnit from '@/components/tempoWithUnit.vue';
import TreeButton from '@/components/treeButton';
import ReplaceOrDeleteHubModal from '@/modals/replaceOrDeleteHubModal';
import FirmwareUpdateModal from '@/modals/firmwareUpdateModal';
import $ from 'jquery';

export default {
    name: 'ComponentEditHub',
    components: {
        TreeButton,
        ExternalInput,
        TempoWithUnit,
        ReplaceOrDeleteHubModal,
        FirmwareUpdateModal,
    },
    setup() {
        const userStore = useUserStore();
        const router = useRouter();
        const route = useRoute();
        const { t } = useI18n();

        const facilityId = parseInt(route.query.facilityId);
        const hubId = parseInt(route.query.id);
        const storeHub = userStore.getHub(facilityId, hubId);
        const hub = reactive(storeHub.clone());

        const inputs = computed(() => {
            const clientStore = useClientStore();
            return clientStore.getInputs();
        });

        const handleSubmit = async () => {
            try {
                await userStore.updateHub(facilityId, storeHub, hub);
                storeHub.update(hub);
                document.getElementById("hub_node").innerHTML = hub.label;
                showToast('success', t('common.updateSuccess'));
            } catch (error) {
                showToast('error', error);
            }
        };

        const pathToInstallationEditComponents = computed(() => {
            return `/installation-edit?id=${facilityId}&action=components`;
        });

        const replaceOrDeleteModal = ref(null);

        const handleReplaceOrDelete = async (reason) => {
            try {
                await userStore.deleteHub(facilityId, storeHub, reason);
                await router.push(pathToInstallationEditComponents.value);
                showToast('success', t('componentEditHub.deleteSuccess', { type: 'HUB' }));
            } catch (error) {
                showToast('error', error);
            }
        };

        // Firmware update of the device, driven by the modal (confirmation, order to the server, result after the reboot)
        const firmwareUpdateModal = ref(null);

        const updateFirmware = async () => {
            const device = hub.productName;
            const result = await firmwareUpdateModal.value.show(facilityId, storeHub);
            if (!result) {
                // Cancelled, or the error was displayed by the modal
                return;
            }
            if (result.version && result.version !== hub.software_version) {
                // Reflect the installed version in the page and in the facility's hub
                hub.software_version = result.version;
                storeHub.software_version = result.version;
            }
            if (result.upToDate) {
                showToast('success', t('componentEditHub.firmwareUpToDate', { device }));
            } else if (result.rebooted) {
                // The device rebooted and reported the new firmware to the server
                showToast('success', t('componentEditHub.firmwareUpdateRebooted', { device }));
            } else {
                showToast('success', t('componentEditHub.firmwareUpdateDone', { device }));
            }
        };

        const antiTimeBackToggle = computed({
            get() {
                return hub.anti_time_back !== 0;
            },
            set(val) {
                hub.anti_time_back = val ? 1 : 0;
            },
        });

        const tree = ref(null);

        onMounted(() => {
            $(tree.value).jstree();

            $(tree.value).on('click', '.tree-button', (event) => {
                const jsonURI = $(event.currentTarget).attr('data-url');
                const uri = JSON.parse(jsonURI);
                router.push({ path: uri.path, query: uri.params });
            });

            const clientStore = useClientStore();
            clientStore.loadInputs(facilityId, hubId);
        });

        return {
            tree,
            facilityId,
            hub,
            pathToInstallationEditComponents,
            antiTimeBackToggle,
            handleSubmit,
            inputs,
            replaceOrDeleteModal,
            handleReplaceOrDelete,
            firmwareUpdateModal,
            updateFirmware,
        };
    },
};
</script>
